import { ChevronDoubleLeftIcon } from '@c/icons';
import { isMobile } from '@util/index';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';

type PaginatorProps = {
  currentPage: number;
  totalPages: number;
  scroll?: boolean;
  setIsFetching?: ((isFetching: boolean) => void) | undefined;
};

const Paginator = ({
  totalPages,
  currentPage,
  scroll,
  setIsFetching,
}: PaginatorProps) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const getPageHref = useCallback(
    (direction: 'first' | 'last' | 'previous' | 'next' | number) => {
      let page;
      if (direction === 'first') {
        page = 1;
      } else if (direction === 'last') {
        page = totalPages;
      } else if (direction === 'previous') {
        page = currentPage - 1;
      } else if (direction === 'next') {
        page = currentPage + 1;
      } else {
        page = direction;
      }

      // every filter the results are narrowed down by lives either in the
      // pathname or in the query string, so the whole query rides along and
      // only the page number moves
      const query = new URLSearchParams(searchParams?.toString() ?? '');
      query.set('page', String(page));

      return `${pathname?.split('?')[0]}?${query.toString()}`;
    },
    [currentPage, pathname, searchParams, totalPages]
  );

  const getPageWindow = () => {
    let before = isMobile() ? 2 : 4;
    let after = isMobile() ? 2 : 4;
    let windowStart = Math.max(1, currentPage - before);
    let windowEnd = Math.min(totalPages, currentPage + after);
    const windowSize = before + after + 1;
    if (windowEnd - windowStart + 1 < windowSize) {
      if (windowStart === 1) {
        windowEnd = Math.min(totalPages, windowStart + windowSize - 1);
      } else if (windowEnd === totalPages) {
        windowStart = Math.max(1, windowEnd - windowSize + 1);
      }
    }
    return { windowStart, windowEnd };
  };

  const getPageButtons = () => {
    const { windowStart, windowEnd } = getPageWindow();
    return Array.from(
      { length: windowEnd - windowStart + 1 },
      (_, i) => windowStart + i
    );
  };

  const onClick = () => {
    window.scrollTo(0, 0);
    setIsFetching?.(true);
  };

  return (
    <>
      <div className="mt-16 flex w-full items-center justify-center gap-x-4 border-y border-zinc-300 px-4 py-8 sm:mt-32 sm:border-none">
        {currentPage > 1 ? (
          <>
            <Link
              aria-label={'First page'}
              href={getPageHref('first')}
              onClick={onClick}
              className="flex h-touch w-touch items-center justify-center rounded-full bg-black/10 pt-1 text-black transition-colors duration-150 sm:bg-transparent"
            >
              <ChevronDoubleLeftIcon className="h-8 w-8" />
            </Link>

            <Link
              aria-label={'Previous page'}
              href={getPageHref('previous')}
              onClick={onClick}
              className="flex h-touch w-[4.5rem] items-center justify-center rounded-full bg-black/10 pt-1 text-black transition-colors duration-150 sm:bg-transparent"
            >
              <ChevronLeftIcon className="h-10 w-10" />
            </Link>
          </>
        ) : (
          <>
            <span className="flex h-touch w-touch items-center justify-center rounded-full pt-1 text-zinc-400">
              <ChevronDoubleLeftIcon className="h-8 w-8" />
            </span>

            <span className="flex h-touch w-touch items-center justify-center rounded-full pt-1 text-zinc-400">
              <ChevronLeftIcon className="h-10 w-10" />
            </span>
          </>
        )}

        {getPageButtons().map((page, index) => {
          return (
            <Link
              aria-label={`Page ${page}`}
              key={page}
              href={getPageHref(Number(page))}
              onClick={onClick}
              className={`flex h-touch w-touch cursor-pointer items-center justify-center rounded-[1rem] pt-1 text-[2rem] font-semibold transition-colors ${
                currentPage === page
                  ? 'bg-black text-white'
                  : 'bg-transparent text-black hover:relative hover:after:absolute hover:after:inset-x-0 hover:after:bottom-0 hover:after:block hover:after:h-2 hover:after:bg-black hover:after:opacity-20 hover:after:content-[""]'
              }`}
            >
              <span>{page}</span>
            </Link>
          );
        })}

        {currentPage < totalPages ? (
          <>
            <Link
              aria-label={'Next page'}
              href={getPageHref('next')}
              onClick={onClick}
              className="flex h-touch w-[4.5rem] items-center justify-center rounded-full bg-black/10 pt-1 text-black transition-colors duration-150 sm:bg-transparent"
            >
              <ChevronRightIcon className="h-10 w-10" />
            </Link>

            <Link
              aria-label={'Last page'}
              href={getPageHref('last')}
              onClick={onClick}
              className="flex h-touch w-touch items-center justify-center rounded-full bg-black/10 pt-1 text-black transition-colors duration-150 sm:bg-transparent"
            >
              <ChevronDoubleLeftIcon className="h-8 w-8 rotate-180" />
            </Link>
          </>
        ) : (
          <>
            <span className="flex h-touch w-touch items-center justify-center rounded-full pt-1 text-zinc-400">
              <ChevronRightIcon className="h-10 w-10" />
            </span>

            <span className="flex h-touch w-touch items-center justify-center rounded-full pt-1 text-zinc-400">
              <ChevronDoubleLeftIcon className="h-8 w-8 rotate-180" />
            </span>
          </>
        )}
      </div>
    </>
  );
};

export default Paginator;

export const PaginatorClient = ({
  totalPages,
  currentPage,
  onPageChange,
  setIsFetching,
}: PaginatorProps & { onPageChange: (n: number) => void }) => {
  const getPageNumber = (
    direction: 'first' | 'last' | 'previous' | 'next' | number
  ) => {
    let page;
    if (direction === 'first') {
      page = 1;
    } else if (direction === 'last') {
      page = totalPages;
    } else if (direction === 'previous') {
      page = currentPage - 1;
    } else if (direction === 'next') {
      page = currentPage + 1;
    } else {
      page = direction;
    }
    return page;
  };

  const getPageWindow = () => {
    let windowStart = Math.max(1, currentPage - 4);
    let windowEnd = Math.min(totalPages, currentPage + 4);
    if (windowEnd - windowStart + 1 < 9) {
      if (windowStart === 1) {
        windowEnd = Math.min(totalPages, windowStart + 8);
      } else if (windowEnd === totalPages) {
        windowStart = Math.max(1, windowEnd - 8);
      }
    }
    return { windowStart, windowEnd };
  };

  const getPageButtons = () => {
    const { windowStart, windowEnd } = getPageWindow();
    return Array.from(
      { length: windowEnd - windowStart + 1 },
      (_, i) => windowStart + i
    );
  };

  const handlePageChange = (page: number) => {
    if (setIsFetching) setIsFetching(true);
    window.scrollTo(0, 0);
    onPageChange(page);
  };

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <button
          aria-label="First page"
          onClick={() => handlePageChange(1)}
          disabled={currentPage <= 1}
          className={`flex h-touch w-touch items-center justify-center rounded-full ${
            currentPage <= 1
              ? 'cursor-not-allowed text-zinc-400'
              : 'text-black hover:bg-black/10'
          }`}
          type="button"
        >
          <ChevronDoubleLeftIcon className="h-10 w-10" />
        </button>

        <button
          aria-label="Previous page"
          onClick={() => handlePageChange(getPageNumber('previous'))}
          disabled={currentPage <= 1}
          className={`flex h-touch w-[4.5rem] items-center justify-center rounded-full pt-1 transition-colors duration-150 ${
            currentPage <= 1
              ? 'cursor-not-allowed text-zinc-400'
              : 'bg-black/10 text-black hover:bg-black/20 sm:bg-transparent'
          }`}
        >
          <ChevronLeftIcon className="h-10 w-10" />
        </button>

        {getPageButtons().map((page, index) => {
          return (
            <button
              aria-label={`Page ${page}`}
              key={page}
              onClick={() => handlePageChange(getPageNumber(Number(page)))}
              className={`flex h-touch w-touch cursor-pointer items-center justify-center rounded-[1rem] pt-1 text-[2rem] font-semibold transition-colors ${
                currentPage === page
                  ? 'bg-black text-white'
                  : 'bg-transparent text-black hover:relative hover:after:absolute hover:after:inset-x-0 hover:after:bottom-0 hover:after:block hover:after:h-2 hover:after:bg-black hover:after:opacity-20 hover:after:content-[""]'
              }`}
            >
              <span>{page}</span>
            </button>
          );
        })}

        <button
          aria-label="Next page"
          onClick={() => handlePageChange(getPageNumber('next'))}
          disabled={currentPage >= totalPages}
          className={`flex h-touch w-[4.5rem] items-center justify-center rounded-full pt-1 transition-colors duration-150 ${
            currentPage >= totalPages
              ? 'cursor-not-allowed text-zinc-400'
              : 'bg-black/10 text-black hover:bg-black/20 sm:bg-transparent'
          }`}
        >
          <ChevronRightIcon className="h-10 w-10" />
        </button>

        <button
          aria-label="Last page"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage >= totalPages}
          className={`flex h-touch w-touch items-center justify-center rounded-full ${
            currentPage >= totalPages
              ? 'cursor-not-allowed text-zinc-400'
              : 'text-black hover:bg-black/10'
          }`}
          type="button"
        >
          <ChevronDoubleLeftIcon className="h-10 w-10 rotate-180" />
        </button>
      </div>
    </>
  );
};
